import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { useAuth } from "../../context/authContext";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./UserInfoPage.module.css";
import ActionModal from "../../components/UI/ActionModal/ActionModal";
import ProfileInfoTab from "../../components/Layout/UserPageTabs/ProfileInfoTab";
import ActivityStatusTab from "../../components/Layout/UserPageTabs/ActivityStatusTab";
import UserActionsTab from "../../components/Layout/UserPageTabs/UserActionsTab";
import InviteUserTab from "../../components/Layout/UserPageTabs/InviteUserTab";
import InvitationStatusTab from "../../components/Layout/UserPageTabs/InvitationStatusTab";
import {
  sendInvitation,
  getAllInvitations,
  cancelInvitation,
} from "../../services/invitation-service";
import {
  getAllUsers,
  updateUserStatus,
  updateUserRole,
} from "../../services/user-service";

export default function UserInfoPage() {
  const { t } = useTranslation("common");
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState({});
  const hasFetchedRef = useRef(false);
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    type: "default",
    title: "",
    message: "",
    onConfirm: null,
    isLoading: false,
  });

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setActionModal({
      isOpen: true,
      type: "warning",
      title: t("userInfoPage.modals.sendInvitation.title"),
      message: t("userInfoPage.modals.sendInvitation.message", {
        email: inviteEmail,
      }),
      onConfirm: async () => {
        setActionModal((prev) => ({ ...prev, isLoading: true }));
        setIsInviting(true);

        try {
          const result = await sendInvitation({
            email: inviteEmail,
            invitedBy: authUser?._id,
          });

          if (result.success) {
            setInviteEmail("");
            setActionModal({
              isOpen: true,
              type: "success",
              title: t("userInfoPage.modals.sendInvitation.successTitle"),
              message: t("userInfoPage.modals.sendInvitation.successMessage", {
                email: inviteEmail,
              }),
              onConfirm: () => {
                setActionModal({
                  isOpen: false,
                  type: "default",
                  title: "",
                  message: "",
                  onConfirm: null,
                  isLoading: false,
                });
                if (activeTab === "users") {
                  fetchInvitations();
                }
              },
              isLoading: false,
            });
          } else {
            setActionModal({
              isOpen: true,
              type: "failure",
              title: t("userInfoPage.modals.sendInvitation.failureTitle"),
              message:
                result.error ||
                t("userInfoPage.modals.sendInvitation.failureMessage"),
              onConfirm: () => {
                setActionModal({
                  isOpen: false,
                  type: "default",
                  title: "",
                  message: "",
                  onConfirm: null,
                  isLoading: false,
                });
              },
              isLoading: false,
            });
          }
        } catch {
          setActionModal({
            isOpen: true,
            type: "failure",
            title: t("userInfoPage.modals.error.title"),
            message: t("userInfoPage.modals.error.invitationSendMessage"),
            onConfirm: () => {
              setActionModal({
                isOpen: false,
                type: "default",
                title: "",
                message: "",
                onConfirm: null,
                isLoading: false,
              });
            },
            isLoading: false,
          });
        } finally {
          setIsInviting(false);
          setActionModal((prev) => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false,
    });
  };

  const fetchInvitations = async () => {
    try {
      const result = await getAllInvitations();

      if (result.success) {
        setInvitations(result.data || []);
      } else {
        setInvitations([]);
      }
    } catch {
      setInvitations([]);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);

    try {
      const usersData = await getAllUsers();

      if (usersData && usersData.success) {
        setAllUsers(usersData.data || []);
      } else {
        setAllUsers([]);
        setUsersError(usersData?.error || "Failed to load users");
      }

      const invitationsResult = await getAllInvitations();

      if (invitationsResult.success) {
        setInvitations(invitationsResult.data);
      }
    } catch (error) {
      setAllUsers([]);
      setUsersError("Failed to load users: " + error.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleToggleUserStatus = (userId, newStatus) => {
    const user = allUsers.find((u) => u._id === userId);
    if (!user) return;

    if (authUser?.role === "admin" && user.role === "owner") {
      setActionModal({
        isOpen: true,
        type: "danger",
        title: t("userInfoPage.modals.toggleUserStatus.accessDeniedTitle"),
        message: t(
          "userInfoPage.modals.toggleUserStatus.adminCannotModifyOwner"
        ),
        onConfirm: () => {
          setActionModal({
            isOpen: false,
            type: "default",
            title: "",
            message: "",
            onConfirm: null,
            isLoading: false,
          });
        },
        isLoading: false,
      });
      return;
    }

    if (authUser?.role !== "admin" && authUser?.role !== "owner") {
      setActionModal({
        isOpen: true,
        type: "danger",
        title: t("userInfoPage.modals.toggleUserStatus.accessDeniedTitle"),
        message: t(
          "userInfoPage.modals.toggleUserStatus.insufficientPermissions"
        ),
        onConfirm: () => {
          setActionModal({
            isOpen: false,
            type: "default",
            title: "",
            message: "",
            onConfirm: null,
            isLoading: false,
          });
        },
        isLoading: false,
      });
      return;
    }

    const userName = user.name || user.email || "this user";

    setActionModal({
      isOpen: true,
      type: "warning",
      title: newStatus
        ? t("userInfoPage.modals.toggleUserStatus.activateTitle")
        : t("userInfoPage.modals.toggleUserStatus.deactivateTitle"),
      message: newStatus
        ? t("userInfoPage.modals.toggleUserStatus.activateMessage", {
            userName,
          })
        : t("userInfoPage.modals.toggleUserStatus.deactivateMessage", {
            userName,
          }),
      onConfirm: async () => {
        setActionModal((prev) => ({ ...prev, isLoading: true }));

        try {
          const response = await updateUserStatus(userId, newStatus);

          if (response.success) {
            const updatedUsers = allUsers.map((user) =>
              user._id === userId ? { ...user, isActive: newStatus } : user
            );
            setAllUsers(updatedUsers);

            setActionModal({
              isOpen: true,
              type: "success",
              title: t("userInfoPage.modals.toggleUserStatus.successTitle"),
              message: newStatus
                ? t("userInfoPage.modals.toggleUserStatus.activatedMessage", {
                    userName,
                  })
                : t("userInfoPage.modals.toggleUserStatus.deactivatedMessage", {
                    userName,
                  }),
              onConfirm: () => {
                setActionModal({
                  isOpen: false,
                  type: "default",
                  title: "",
                  message: "",
                  onConfirm: null,
                  isLoading: false,
                });
              },
              isLoading: false,
            });
          } else {
            throw new Error(response.error || "Failed to update user status");
          }
        } catch {
          setActionModal({
            isOpen: true,
            type: "failure",
            title: t("userInfoPage.modals.error.title"),
            message: t("userInfoPage.modals.error.generalMessage"),
            onConfirm: () => {
              setActionModal({
                isOpen: false,
                type: "default",
                title: "",
                message: "",
                onConfirm: null,
                isLoading: false,
              });
            },
            isLoading: false,
          });
        } finally {
          setActionModal((prev) => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false,
    });
  };

  const handleRoleSelect = (userId, role) => {
    setSelectedRoles((prev) => ({
      ...prev,
      [userId]: role,
    }));
  };

  const handleRoleChange = (userId, newRole) => {
    if (authUser?.role !== "owner") {
      setActionModal({
        isOpen: true,
        type: "danger",
        title: t("userInfoPage.modals.changeRole.accessDeniedTitle"),
        message: t("userInfoPage.modals.changeRole.accessDeniedMessage"),
        onConfirm: () => {
          setActionModal({
            isOpen: false,
            type: "default",
            title: "",
            message: "",
            onConfirm: null,
            isLoading: false,
          });
        },
        isLoading: false,
      });
      return;
    }

    if (newRole === "owner") {
      setActionModal({
        isOpen: true,
        type: "danger",
        title: t("userInfoPage.modals.changeRole.invalidRoleTitle"),
        message: t("userInfoPage.modals.changeRole.invalidRoleMessage"),
        onConfirm: () => {
          setActionModal({
            isOpen: false,
            type: "default",
            title: "",
            message: "",
            onConfirm: null,
            isLoading: false,
          });
        },
        isLoading: false,
      });
      return;
    }

    const targetUser = allUsers.find((u) => u._id === userId);
    if (!targetUser) return;

    const userName = targetUser.name || targetUser.email || "this user";

    setActionModal({
      isOpen: true,
      type: "warning",
      title: t("userInfoPage.modals.changeRole.title"),
      message: t("userInfoPage.modals.changeRole.message", {
        userName,
        currentRole: targetUser.role,
        newRole,
      }),
      onConfirm: async () => {
        setActionModal((prev) => ({ ...prev, isLoading: true }));

        try {
          const response = await updateUserRole(userId, newRole);

          if (response.success) {
            const updatedUsers = allUsers.map((user) =>
              user._id === userId ? { ...user, role: newRole } : user
            );
            setAllUsers(updatedUsers);

            setSelectedRoles((prev) => {
              const newSelected = { ...prev };
              delete newSelected[userId];
              return newSelected;
            });

            setActionModal({
              isOpen: true,
              type: "success",
              title: t("userInfoPage.modals.changeRole.successTitle"),
              message: t("userInfoPage.modals.changeRole.successMessage", {
                userName,
                newRole,
              }),
              onConfirm: () => {
                setActionModal({
                  isOpen: false,
                  type: "default",
                  title: "",
                  message: "",
                  onConfirm: null,
                  isLoading: false,
                });
              },
              isLoading: false,
            });
          } else {
            throw new Error(response.error || "Failed to update user role");
          }
        } catch {
          setActionModal({
            isOpen: true,
            type: "failure",
            title: t("userInfoPage.modals.error.title"),
            message: t("userInfoPage.modals.error.roleUpdateMessage"),
            onConfirm: () => {
              setActionModal({
                isOpen: false,
                type: "default",
                title: "",
                message: "",
                onConfirm: null,
                isLoading: false,
              });
            },
            isLoading: false,
          });
        } finally {
          setActionModal((prev) => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false,
    });
  };

  const handleCancelInvitation = (invitationId, email) => {
    setActionModal({
      isOpen: true,
      type: "warning",
      title: t("userInfoPage.modals.cancelInvitation.title"),
      message: t("userInfoPage.modals.cancelInvitation.message", { email }),
      onConfirm: async () => {
        setActionModal((prev) => ({ ...prev, isLoading: true }));

        try {
          const response = await cancelInvitation(invitationId);

          if (response.success) {
            const updatedInvitations = invitations.map((invitation) =>
              invitation._id === invitationId
                ? { ...invitation, status: "cancelled" }
                : invitation
            );
            setInvitations(updatedInvitations);

            setActionModal({
              isOpen: true,
              type: "success",
              title: t("userInfoPage.modals.cancelInvitation.successTitle"),
              message: t(
                "userInfoPage.modals.cancelInvitation.successMessage",
                { email }
              ),
              onConfirm: () => {
                setActionModal({
                  isOpen: false,
                  type: "default",
                  title: "",
                  message: "",
                  onConfirm: null,
                  isLoading: false,
                });
              },
              isLoading: false,
            });
          } else {
            throw new Error(response.error || "Failed to cancel invitation");
          }
        } catch {
          setActionModal({
            isOpen: true,
            type: "failure",
            title: t("userInfoPage.modals.error.title"),
            message: t("userInfoPage.modals.error.invitationCancelMessage"),
            onConfirm: () => {
              setActionModal({
                isOpen: false,
                type: "default",
                title: "",
                message: "",
                onConfirm: null,
                isLoading: false,
              });
            },
            isLoading: false,
          });
        } finally {
          setActionModal((prev) => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false,
    });
  };

  useEffect(() => {
    if (
      (activeTab === "profile" ||
        activeTab === "activity" ||
        activeTab === "actions" ||
        activeTab === "invitation-status") &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true;
      fetchUsers();
    }
  }, [activeTab]);

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={
            <Users size={28} strokeWidth={1.75} absoluteStrokeWidth />
          }
          title={t("userInfoPage.title")}
          subtitle={t("userInfoPage.subtitle")}
        />

        <div className={styles.main}>
          <div className={styles.tabNavigation}>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "profile" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("profile");
                hasFetchedRef.current = false;
              }}
            >
              {t("userInfoPage.tabs.profileInfo")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "activity" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("activity");
                hasFetchedRef.current = false;
              }}
            >
              {t("userInfoPage.tabs.activityStatus")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "actions" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("actions");
                hasFetchedRef.current = false;
              }}
            >
              {t("userInfoPage.tabs.userActions")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "invite" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("invite")}
            >
              {t("userInfoPage.tabs.inviteUser")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "invitation-status" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("invitation-status");
                fetchInvitations();
              }}
            >
              {t("userInfoPage.tabs.invitationStatus")}
            </button>
          </div>

          {activeTab === "invite" && (
            <InviteUserTab
              inviteEmail={inviteEmail}
              setInviteEmail={setInviteEmail}
              isInviting={isInviting}
              handleInviteUser={handleInviteUser}
            />
          )}

          {activeTab === "profile" && (
            <ProfileInfoTab
              allUsers={allUsers}
              loadingUsers={loadingUsers}
              usersError={usersError}
              fetchUsers={fetchUsers}
              hasFetchedRef={hasFetchedRef}
            />
          )}

          {activeTab === "activity" && (
            <ActivityStatusTab
              allUsers={allUsers}
              loadingUsers={loadingUsers}
              usersError={usersError}
              fetchUsers={fetchUsers}
              hasFetchedRef={hasFetchedRef}
              invitations={invitations}
              handleCancelInvitation={handleCancelInvitation}
            />
          )}

          {activeTab === "actions" && (
            <UserActionsTab
              allUsers={allUsers}
              loadingUsers={loadingUsers}
              usersError={usersError}
              fetchUsers={fetchUsers}
              hasFetchedRef={hasFetchedRef}
              authUser={authUser}
              selectedRoles={selectedRoles}
              handleRoleSelect={handleRoleSelect}
              handleRoleChange={handleRoleChange}
              handleToggleUserStatus={handleToggleUserStatus}
            />
          )}

          {activeTab === "invitation-status" && (
            <InvitationStatusTab
              invitations={invitations}
              loadingUsers={loadingUsers}
              usersError={usersError}
              fetchUsers={fetchUsers}
              hasFetchedRef={hasFetchedRef}
              handleCancelInvitation={handleCancelInvitation}
            />
          )}
        </div>
      </div>

      <ActionModal
        isOpen={actionModal.isOpen}
        onClose={() =>
          setActionModal({
            isOpen: false,
            type: "default",
            title: "",
            message: "",
            onConfirm: null,
            isLoading: false,
          })
        }
        onConfirm={actionModal.onConfirm}
        title={actionModal.title}
        message={actionModal.message}
        type={actionModal.type}
        confirmText={t("confirm")}
        cancelText={t("cancel")}
        isLoading={actionModal.isLoading}
      />
    </div>
  );
}
